import { lddGlobalSessionInfo, NavObjectType } from "../navigationTypes";
import { Tag, Document, PSM, P, S, M, Creator, User, Custom, System, Category, CompoundDocument, Package } from "./types/Types";
import { CategoryMap } from "./types/Category"; 

/**
 * Get the document list from rwapi
 * @param {string} prov province
 * @param {string} accId account/firm id
 * @param {string} sessionId 
 * @returns {Document[]} document list
 * @returns {Category[]} category list that response document list contains
 */
export async function getDocumentList(prov: string, accId: string, sessionId: string): Promise<{ docList: Document[], categoryList: Category[] }> {
    const res = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/docmasters/${prov}/${accId}/documentlist?SessionID=${sessionId}`)
        .then((res) => {
            if (res.status === 200 && res.ok) {
                return res;
            }
            throw new Error("Network response was not ok.");
        });

    const data = await res.json();
    if (data.ok) {
        const docList: Document[] = [];
        const categoryList: Category[] = [];

        function attachCategory(doc: Document, category_id: number, prov: string, categoryList: Category[]) {
            const categories = CategoryMap[prov][category_id];
            if (categories && categories.length > 0) {
                doc.tags.push(...categories);
                categories.forEach((category) => {
                    if (categoryList.indexOf(category) === -1) {
                        categoryList.push(category);
                    }
                });
            }
            categoryList.sort((a, b) => a.name.localeCompare(b.name));
        }

        for (let i = 0; i < data.result.length; i++) {
            const doc: Document = data.result[i];
            doc.tags = [];

            // skip category_id 12 as it indicates hidden documents that only accessible by admin site
            // add admin site check here once deployed on admin site
            if (data.result[i].category_id === 12)
                continue;

            // add PSM tags
            if (data.result[i].psm.toString().includes("P"))
                doc.tags.push(P);
            if (data.result[i].psm.toString().includes("S"))
                doc.tags.push(S);
            if (data.result[i].psm.toString().includes("M"))
                doc.tags.push(M);

            // add creator tags
            if (data.result[i].is_user_owned)
                doc.tags.push(User);
            else if (data.result[i].is_customized)
                doc.tags.push(Custom);
            else
                doc.tags.push(System);

            // province specific tags
            switch (prov) {
                case "AB":
                case "MB":
                case "NS":
                case "SK":
                    switch (data.result[i].template_type_id) {
                        case 11:    // compound document
                            doc.tags.push(CompoundDocument);
                            doc.is_uneditable = true;
                            break;
                        default:
                            break;
                    }
                    break;
                default:
                    break;
            }

            // general tags
            switch (data.result[i].template_type_id) {
                case 10:    // PDF
                    doc.is_uneditable = true;
                    break;
                default:
                    break;
            }

            // map category_id to Category and attach as tags
            attachCategory(doc, data.result[i].category_id, prov, categoryList);

            // bfs to push all parent category names as id to doc.tags
            const curCatQueue = Array<Category>(doc.tags.find((tag) => tag && 'category_id' in tag) as Category);
            while (curCatQueue.length > 0) {
                const curCat: Category = curCatQueue.shift();
                if (curCat?.parentNames?.length > 0) {
                    curCat.parentNames.forEach((prtCatName) => {
                        const parentCat = categoryList.find((cat) => cat.name === prtCatName) as Category
                        curCatQueue.push(parentCat);
                        if (doc.tags.indexOf(parentCat) === -1)
                            doc.tags.push(parentCat);
                    });
                }
            }
            docList.push(doc);
        }
        return { docList, categoryList };
    } else {
        throw new Error(data.error);
    }
}

export function navDocEditor(doc: Document, SESSION_INFO: lddGlobalSessionInfo, nav?: (NavObjectType) => void): void {
    const navigation: NavObjectType = {
        component: "main", 
        url: "lfmaster.asp", 
        params: { 
            'formasp': 'EditDocument', 
            "src": "getdocumentdata.asp?doctypeid=" + doc.doc_type_id.toString() + "&docid=" + doc.doc_id,
            "save": "",
            "SessionID": SESSION_INFO.sid,
            "doctypeid": doc.doc_type_id.toString(),
            "lddbase": doc.tags.includes(User) ? "0" : "1",
        },
        init: true
    };

    return nav(navigation);
}

export function hasEveryTags(objs: Document | Package | Array<Document | Package>, tags: Tag | PSM | Creator | Category | Package | Array<Tag | PSM | Creator | Category | Package>): boolean {
    const objsArray = Array.isArray(objs) ? objs : [objs];
    const tagsArray = Array.isArray(tags) ? tags : [tags];
    return tagsArray.every(tag => objsArray.every(obj => obj.tags?.includes(tag)));
}

export function hasSomeTags(objs: Document | Package | Array<Document | Package>, tags: Tag | PSM | Creator | Category | Package | Array<Tag | PSM | Creator | Category | Package>): boolean {
    const objsArray = Array.isArray(objs) ? objs : [objs];
    const tagsArray = Array.isArray(tags) ? tags : [tags];
    return tagsArray.some(tag => objsArray.every(obj => obj.tags?.includes(tag)));
}